import React from 'react';
import type { Recipient } from '@/types';
import { type TelegramSenderMode } from '@/utils/telegramErrors';
import {
  readinessTitle,
  resolveRecipientReadinessTransport,
} from '@/utils/recipientReadiness';
import StatusBadge from './StatusBadge';

type Props = {
  recipient: Recipient;
  botConfigured: boolean;
  telethonConfigured: boolean;
  telethonSessionReady: boolean;
  senderMode: TelegramSenderMode;
};

export default function RecipientReadinessBadge({
  recipient,
  botConfigured,
  telethonConfigured,
  telethonSessionReady,
  senderMode,
}: Props) {
  if (recipient.channel !== 'telegram') return null;

  const ctx = {
    botConfigured,
    telethonConfigured,
    telethonSessionReady,
    senderMode,
  };

  if (!recipient.is_enabled) {
    return (
      <StatusBadge variant="neutral" title="Recipient is disabled — no deliveries">
        DISABLED
      </StatusBadge>
    );
  }

  const transport = resolveRecipientReadinessTransport(recipient, ctx);
  const title = readinessTitle(recipient, transport, ctx);
  const provider = recipient.delivery_provider || 'auto';

  if (transport === 'telethon') {
    return (
      <StatusBadge variant="success" title={title}>
        READY · TELETHON
      </StatusBadge>
    );
  }

  if (transport === 'bot') {
    return (
      <StatusBadge variant="success" title={title}>
        READY · BOT
      </StatusBadge>
    );
  }

  if (provider === 'telethon' && !telethonConfigured) {
    return (
      <StatusBadge variant="danger" title="Telethon delivery selected but session/credentials are not configured">
        TELETHON NOT READY
      </StatusBadge>
    );
  }

  if (provider === 'bot' && !botConfigured) {
    return (
      <StatusBadge variant="danger" title="Bot API delivery selected but bot token is not configured">
        BOT NOT READY
      </StatusBadge>
    );
  }

  if ((recipient.chat_id || '').replace(/^@+/, '') && recipient.chat_id !== 'me') {
    const chat = recipient.chat_id || '';
    if (/^[a-zA-Z][a-zA-Z0-9_]{4,31}$/.test(chat.replace(/^@+/, ''))) {
      return (
        <StatusBadge variant="warning" title={title}>
          NEEDS START
        </StatusBadge>
      );
    }
  }

  return (
    <StatusBadge variant="neutral" title={title}>
      UNKNOWN
    </StatusBadge>
  );
}
